import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { PAGE_CONTENT_CLASS } from "components/layout/PageContent";
import PageHeader from "components/PageHeader";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import LoadingMark from "components/ui/LoadingMark";
import { cn } from "utils/cn";
import {
  CRM_STAGE_OPTIONS,
  getStageLabel,
  mergeStageOptions,
} from "constants/crm";
import crmService from "services/crmService";
import LeadDetailDrawer from "./LeadDetailDrawer";

const STAGE_ACCENT = {
  novo: "bg-sky-500",
  qualificado: "bg-indigo-500",
  visita: "bg-amber-500",
  proposta: "bg-violet-500",
  ganho: "bg-emerald-500",
  perdido: "bg-slate-400",
};

function isFollowUpDue(followUpAt) {
  if (!followUpAt) return false;
  const d = new Date(followUpAt);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() <= Date.now();
}

function formatFollowUp(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function LeadCard({
  lead,
  stageOptions,
  dragging,
  canDrag,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
  onWhatsApp,
}) {
  const followUp = formatFollowUp(lead.follow_up_at);
  const due = isFollowUpDue(lead.follow_up_at);

  return (
    <article
      draggable={canDrag}
      onDragStart={canDrag ? (e) => onDragStart(e, lead.id) : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={() => onOpen(lead)}
      className={cn(
        "group rounded-xl border border-border/80 bg-card p-3.5 transition-smooth",
        "shadow-soft-sm hover:border-primary/25 hover:shadow-soft-md",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        canDrag && "md:cursor-grab md:active:cursor-grabbing",
        dragging && "opacity-40 scale-[0.98]"
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground truncate">
            {lead.name || "Sem nome"}
          </p>
          {lead.whatsapp_display ? (
            <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
              {lead.whatsapp_display}
            </p>
          ) : null}
        </div>
        {canDrag ? (
          <span
            className="hidden md:inline-flex shrink-0 text-muted-foreground/50 group-hover:text-muted-foreground"
            aria-hidden
            title="Arrastar"
          >
            <Icon name="GripVertical" size={16} />
          </span>
        ) : null}
      </div>

      {(lead.listing || lead.interest) && (
        <p className="mt-2 text-xs leading-relaxed text-foreground/75 line-clamp-2">
          {lead.listing || lead.interest}
        </p>
      )}

      {followUp ? (
        <p
          className={cn(
            "mt-2.5 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium",
            due
              ? "bg-amber-500/15 text-amber-900 dark:text-amber-200"
              : "bg-muted text-muted-foreground"
          )}
        >
          <Icon name="Clock" size={12} />
          {followUp}
          {due ? " · atrasado" : null}
        </p>
      ) : null}

      <div
        className="mt-3 flex items-center gap-2 border-t border-border/60 pt-2.5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Button
          type="button"
          size="xs"
          variant="outline"
          className="h-8 gap-1.5 px-2.5"
          title="Abrir WhatsApp"
          disabled={!lead.whatsapp_url}
          onClick={() => onWhatsApp(lead)}
        >
          <Icon name="MessageCircle" size={14} />
          <span className="sm:inline">WhatsApp</span>
        </Button>

        <label className="sr-only" htmlFor={`stage-${lead.id}`}>
          Mover estágio
        </label>
        <select
          id={`stage-${lead.id}`}
          className="ml-auto h-8 min-w-0 flex-1 max-w-[11rem] rounded-lg border border-input bg-background px-2 text-xs text-foreground"
          value={lead.stage || "novo"}
          title="Mover para estágio"
          onChange={(e) => onMove(lead, e.target.value)}
        >
          {stageOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
    </article>
  );
}

function ColumnBody({
  column,
  stageOptions,
  dropActive,
  canDrag,
  draggingId,
  emptyHint,
  renaming,
  renameValue,
  renameSaving,
  onRenameStart,
  onRenameChange,
  onRenameCancel,
  onRenameSubmit,
  onOpen,
  onMove,
  onDragStart,
  onDragEnd,
  onWhatsApp,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  return (
    <section
      className={cn(
        "flex min-h-0 flex-col rounded-2xl border bg-muted/25",
        dropActive ? "border-primary/40 bg-primary/[0.06] ring-2 ring-primary/20" : "border-border/70"
      )}
      onDragOver={canDrag ? (e) => onDragOver(e, column.value) : undefined}
      onDragLeave={canDrag ? onDragLeave : undefined}
      onDrop={canDrag ? (e) => onDrop(e, column.value) : undefined}
      aria-label={`Coluna ${column.label}`}
    >
      <header className="flex shrink-0 items-center gap-2 px-3.5 py-3">
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full shrink-0",
            STAGE_ACCENT[column.value] || "bg-muted-foreground"
          )}
          aria-hidden
        />
        {renaming ? (
          <form
            className="min-w-0 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              onRenameSubmit(column.value);
            }}
          >
            <input
              autoFocus
              disabled={renameSaving}
              value={renameValue}
              maxLength={40}
              aria-label={`Renomear estágio ${column.label}`}
              className="h-8 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm font-semibold text-foreground"
              onChange={(e) => onRenameChange(e.target.value)}
              onBlur={() => onRenameSubmit(column.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  onRenameCancel();
                }
              }}
            />
          </form>
        ) : (
          <>
            <h2 className="min-w-0 truncate text-sm font-semibold tracking-tight text-foreground">
              {column.label}
            </h2>
            <button
              type="button"
              className="shrink-0 rounded-md p-1 text-muted-foreground/70 hover:bg-background hover:text-foreground"
              title="Renomear coluna"
              aria-label={`Renomear ${column.label}`}
              onClick={() => onRenameStart(column)}
            >
              <Icon name="Pencil" size={13} />
            </button>
          </>
        )}
        <span className="ml-auto rounded-full bg-background/80 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground border border-border/60">
          {column.leads.length}
        </span>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-3">
        {column.leads.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border/80 bg-background/40 px-3 py-8 text-center">
            <p className="text-xs text-muted-foreground leading-relaxed">
              {emptyHint}
            </p>
          </div>
        ) : (
          column.leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              stageOptions={stageOptions}
              dragging={draggingId === lead.id}
              canDrag={canDrag}
              onOpen={onOpen}
              onMove={onMove}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onWhatsApp={onWhatsApp}
            />
          ))
        )}
      </div>
    </section>
  );
}

const CrmLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [stageOptions, setStageOptions] = useState(CRM_STAGE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState("");
  const [hideClosed, setHideClosed] = useState(false);
  const [mobileStage, setMobileStage] = useState("novo");
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("edit");
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropStage, setDropStage] = useState(null);
  const [canDrag, setCanDrag] = useState(false);
  const [renamingStage, setRenamingStage] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px) and (hover: hover)");
    const sync = () => setCanDrag(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setListError(null);
    const [contactsResult, stagesResult] = await Promise.all([
      crmService.listContacts(),
      crmService.listStages(),
    ]);

    if (stagesResult.success) {
      setStageOptions(mergeStageOptions(stagesResult.data));
    }

    if (!contactsResult.success) {
      setListError(
        typeof contactsResult.error === "string"
          ? contactsResult.error
          : contactsResult.error?.message || "Erro ao carregar leads"
      );
      setLeads([]);
      setLoading(false);
      return;
    }
    setLeads(contactsResult.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const filtered = useMemo(() => {
    let list = leads;
    if (hideClosed) {
      list = list.filter((l) => l.stage !== "ganho" && l.stage !== "perdido");
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.whatsapp_display || "").includes(q) ||
        (l.listing || "").toLowerCase().includes(q) ||
        (l.interest || "").toLowerCase().includes(q)
    );
  }, [leads, query, hideClosed]);

  const columns = useMemo(() => {
    const stages = hideClosed
      ? stageOptions.filter(
          (s) => s.value !== "ganho" && s.value !== "perdido"
        )
      : stageOptions;
    return stages.map((stage) => ({
      ...stage,
      leads: filtered.filter((l) => (l.stage || "novo") === stage.value),
    }));
  }, [filtered, hideClosed, stageOptions]);

  useEffect(() => {
    if (!columns.some((c) => c.value === mobileStage)) {
      setMobileStage(columns[0]?.value || "novo");
    }
  }, [columns, mobileStage]);

  const mobileColumn = useMemo(
    () => columns.find((c) => c.value === mobileStage) || columns[0],
    [columns, mobileStage]
  );

  const openCreate = () => {
    setDrawerMode("create");
    setSelectedLead(null);
    setDrawerOpen(true);
  };

  const openEdit = (lead) => {
    setDrawerMode("edit");
    setSelectedLead(lead);
    setDrawerOpen(true);
  };

  const openWhatsApp = (lead) => {
    if (lead.whatsapp_url) {
      window.open(lead.whatsapp_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const result = await crmService.saveContact(payload);
      if (!result.success) {
        throw new Error(
          typeof result.error === "string"
            ? result.error
            : result.error?.message || "Erro ao salvar lead"
        );
      }
      toast.success(payload.id ? "Lead atualizado." : "Lead criado.");
      setDrawerOpen(false);
      await loadBoard();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lead) => {
    const ok = window.confirm(
      `Excluir o lead${lead?.name ? ` "${lead.name}"` : ""}? Esta ação não pode ser desfeita.`
    );
    if (!ok) return;
    setSaving(true);
    try {
      const result = await crmService.deleteContact(lead.id);
      if (!result.success) {
        toast.error(
          typeof result.error === "string"
            ? result.error
            : "Erro ao excluir lead"
        );
        return;
      }
      toast.success("Lead excluído.");
      setDrawerOpen(false);
      await loadBoard();
    } finally {
      setSaving(false);
    }
  };

  const startRenameStage = (column) => {
    setRenamingStage(column.value);
    setRenameValue(column.label);
  };

  const cancelRenameStage = () => {
    setRenamingStage(null);
    setRenameValue("");
  };

  const submitRenameStage = async (stageValue) => {
    if (renameSaving || renamingStage !== stageValue) return;
    const nextLabel = renameValue.trim();
    const current = stageOptions.find((s) => s.value === stageValue);
    if (!nextLabel || !current || nextLabel === current.label) {
      cancelRenameStage();
      return;
    }

    setRenameSaving(true);
    const previous = stageOptions;
    setStageOptions((list) =>
      list.map((s) => (s.value === stageValue ? { ...s, label: nextLabel } : s))
    );

    const result = await crmService.saveStage({
      value: stageValue,
      label: nextLabel,
    });

    setRenameSaving(false);
    if (!result.success) {
      setStageOptions(previous);
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Não foi possível renomear o estágio"
      );
      return;
    }

    if (result.data?.label) {
      setStageOptions((list) =>
        list.map((s) =>
          s.value === stageValue ? { ...s, label: result.data.label } : s
        )
      );
    }
    cancelRenameStage();
    toast.success("Coluna renomeada.");
  };

  const moveLeadToStage = async (lead, newStage) => {
    if (!lead || !newStage || lead.stage === newStage) return;
    const previous = leads;
    setLeads((list) =>
      list.map((l) => (l.id === lead.id ? { ...l, stage: newStage } : l))
    );

    const result = await crmService.saveContact({
      id: lead.id,
      name: lead.name,
      whatsapp: lead.whatsapp_e164 || lead.whatsapp_display,
      email: lead.email,
      notes: lead.notes,
      interest: lead.interest,
      listing: lead.listing,
      stage: newStage,
      follow_up_at: lead.follow_up_at || null,
    });

    if (!result.success) {
      setLeads(previous);
      toast.error(
        typeof result.error === "string"
          ? result.error
          : "Não foi possível mover o lead"
      );
      return;
    }

    if (result.data) {
      setLeads((list) =>
        list.map((l) => (l.id === result.data.id ? result.data : l))
      );
    }
  };

  const handleDragStart = (e, leadId) => {
    setDraggingId(leadId);
    e.dataTransfer.setData("text/plain", leadId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropStage(null);
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropStage(stage);
  };

  const handleDrop = async (e, stage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain") || draggingId;
    setDropStage(null);
    setDraggingId(null);
    const lead = leads.find((l) => l.id === leadId);
    if (lead) await moveLeadToStage(lead, stage);
  };

  const columnProps = {
    stageOptions,
    canDrag,
    draggingId,
    renameValue,
    renameSaving,
    onRenameStart: startRenameStage,
    onRenameChange: setRenameValue,
    onRenameCancel: cancelRenameStage,
    onRenameSubmit: submitRenameStage,
    onOpen: openEdit,
    onMove: moveLeadToStage,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onWhatsApp: openWhatsApp,
    onDragOver: handleDragOver,
    onDragLeave: () => setDropStage(null),
    onDrop: handleDrop,
  };

  return (
    <>
      <div className={`${PAGE_CONTENT_CLASS} max-w-none flex flex-col min-h-[calc(100vh-4rem)]`}>
        <Helmet>
          <title>Leads — CRM Neo Documentos</title>
        </Helmet>

        <PageHeader
          title="Leads"
          subtitle="Pipeline sincronizado com a extensão WhatsApp"
          icon="Kanban"
          action={
            <Button type="button" onClick={openCreate} className="gap-2">
              <Icon name="UserPlus" size={18} />
              Novo lead
            </Button>
          }
        />

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-md">
            <Input
              placeholder="Buscar por nome, telefone, empreendimento…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <label className="inline-flex items-center gap-2 self-start text-sm text-muted-foreground cursor-pointer select-none rounded-lg border border-border/70 bg-card px-3 py-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-input"
              checked={hideClosed}
              onChange={(e) => setHideClosed(e.target.checked)}
            />
            Ocultar {getStageLabel("ganho", stageOptions)} /{" "}
            {getStageLabel("perdido", stageOptions)}
          </label>
        </div>

        {loading && (
          <div className="flex justify-center py-10">
            <LoadingMark message="Carregando leads…" />
          </div>
        )}

        {!loading && listError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {listError}
          </div>
        )}

        {!loading && !listError && (
          <>
            {/* Mobile: stage switcher + single column */}
            <div className="md:hidden flex min-h-0 flex-1 flex-col gap-3">
              <div
                className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 snap-x snap-mandatory"
                role="tablist"
                aria-label="Estágios do funil"
              >
                {columns.map((column) => {
                  const active = column.value === mobileStage;
                  return (
                    <button
                      key={column.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setMobileStage(column.value)}
                      className={cn(
                        "snap-start shrink-0 inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-smooth",
                        active
                          ? "border-primary/30 bg-primary text-primary-foreground shadow-soft-sm"
                          : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          active ? "bg-primary-foreground/90" : STAGE_ACCENT[column.value]
                        )}
                        aria-hidden
                      />
                      {column.label}
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[11px] tabular-nums",
                          active
                            ? "bg-primary-foreground/20 text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {column.leads.length}
                      </span>
                    </button>
                  );
                })}
              </div>

              {mobileColumn ? (
                <div className="min-h-0 flex-1">
                  <ColumnBody
                    column={mobileColumn}
                    renaming={renamingStage === mobileColumn.value}
                    dropActive={false}
                    emptyHint="Nenhum lead neste estágio. Use o seletor no card para mover."
                    {...columnProps}
                  />
                </div>
              ) : null}
            </div>

            {/* Desktop: horizontal board */}
            <div className="hidden md:block min-h-0 flex-1">
              <div className="grid h-full min-h-[28rem] grid-flow-col auto-cols-[minmax(16.5rem,1fr)] gap-3 overflow-x-auto pb-2">
                {columns.map((column) => (
                  <ColumnBody
                    key={column.value}
                    column={column}
                    renaming={renamingStage === column.value}
                    dropActive={dropStage === column.value}
                    emptyHint="Arraste um lead para cá"
                    {...columnProps}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Dica: arraste os cards entre colunas, use o lápis para renomear ou o seletor de estágio em cada card.
              </p>
            </div>
          </>
        )}

        {!loading && !listError && leads.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            Nenhum lead ainda. Cadastre pelo web ou pela extensão no WhatsApp —
            o pipeline fica sincronizado automaticamente.
          </p>
        )}
      </div>

      <LeadDetailDrawer
        open={drawerOpen}
        mode={drawerMode}
        lead={selectedLead}
        stageOptions={stageOptions}
        saving={saving}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  );
};

export default CrmLeadsPage;
